// SWIFT AND FOUNDATION BEHAVIOUR THE VALUATION PORT REPRODUCES (W1, R190).
//
// JS and Swift share IEEE-754 doubles, so the same arithmetic in the same order yields the same bits. What they do not
// share is what sits on top of it: the rounding helper, integer conversion, `min`/`max` on signed zero, the currency
// formatter, printf, trimming, case, character counting. Each function below names the Swift it mirrors. Every
// behaviour quoted beside one was MEASURED by a Swift probe on this machine (swift 6.3.3, macOS 26 Foundation, process
// locale en_US, 2026-09-15) running the app's own expressions; functions/test/v2/valuationFormat.test.ts pins those
// values. The parity corpus is recorded on the iOS simulator, and where it disagrees with a macOS measurement it wins.

// ── Rounding, conversion, ordering ──────────────────────────────────────────────────────────────────────────────────

/** `Double.rounded()`: to nearest, ties away from zero. Measured: 2.5 → 3.0, -2.5 → -3.0, -0.5 → -1.0, -0.4 → -0.0,
 *  0.49999999999999994 → 0.0. `Math.round` breaks ties toward +∞ (measured: -2.5 → -2), which is the same rule for
 *  positive input only, so a negative is rounded by magnitude and negated — that also keeps -0.4 → -0. */
export function swiftRounded(x: number): number {
  return x < 0 ? -Math.round(-x) : Math.round(x);
}

/** `Int(x)` from a Double: truncation toward zero, with no negative zero (measured: `Int(-0.0)` → 0, `Int(1850.9)` →
 *  1850, `Int(-3.7)` → -3). Swift traps on a non-finite or out-of-range value, and so does this. */
export function swiftInt(x: number): number {
  if (!Number.isFinite(x) || Math.abs(x) >= 9223372036854775808) {
    throw new RangeError(`swiftInt: Int(${x}) traps in Swift`);
  }
  const t = Math.trunc(x);
  return t === 0 ? 0 : t;
}

/** Swift `Int` division: truncates toward zero (measured: (-1) / 3 → 0, 7 / 2 → 3). */
export function intDiv(a: number, b: number): number {
  return swiftInt(a / b);
}

/** Two-argument `max`. Measured: max(0.0, -0.0) → -0.0 and max(-0.0, 0.0) → 0.0, which is `y >= x ? y : x`;
 *  `Math.max` returns +0 for both. */
export function swiftMax(x: number, y: number): number {
  return y >= x ? y : x;
}

/** Two-argument `min`. Measured: min(0.0, -0.0) → 0.0 and min(-0.0, 0.0) → -0.0, which is `y < x ? y : x`. */
export function swiftMin(x: number, y: number): number {
  return y < x ? y : x;
}

/** Variadic `min(x, y, z, …)`. Measured: min(0.0, -0.0, 0.0) → 0.0, min(-0.0, 0.0, 0.0) → -0.0, min(1.0, 0.0, -0.0)
 *  → 0.0, min(1.0, 2.0, 0.0, -0.0) → 0.0 — a running two-argument `min` from the left gives all four. */
export function swiftMinOf(first: number, ...rest: number[]): number {
  let m = first;
  for (const v of rest) m = swiftMin(m, v);
  return m;
}

/** `Sequence.min()`: kept until a later element is `<` it. Measured: [0.0, -0.0] → 0.0, [-0.0, 0.0] → -0.0. */
export function sequenceMin(xs: readonly number[]): number | null {
  if (xs.length === 0) return null;
  let m = xs[0];
  for (let i = 1; i < xs.length; i++) if (xs[i] < m) m = xs[i];
  return m;
}

/** `Sequence.max()`: replaced only by a later element it is `<`. Measured: [0.0, -0.0] → 0.0, [-0.0, 0.0] → -0.0. */
export function sequenceMax(xs: readonly number[]): number | null {
  if (xs.length === 0) return null;
  let m = xs[0];
  for (let i = 1; i < xs.length; i++) if (m < xs[i]) m = xs[i];
  return m;
}

/** `Sequence.min(by:)`: the FIRST minimal element (measured on 200 elements with three-way ties). */
export function minBy<T>(xs: readonly T[], areInIncreasingOrder: (a: T, b: T) => boolean): T | null {
  if (xs.length === 0) return null;
  let m = xs[0];
  for (let i = 1; i < xs.length; i++) if (areInIncreasingOrder(xs[i], m)) m = xs[i];
  return m;
}

/** `sorted(by:)`. Measured stable on 12 and on 200 elements, ascending and descending; `Array.prototype.sort` is stable
 *  as well. A strict weak ordering becomes a comparator by asking the predicate both ways. */
export function sortedBy<T>(xs: readonly T[], areInIncreasingOrder: (a: T, b: T) => boolean): T[] {
  return [...xs].sort((a, b) => (areInIncreasingOrder(a, b) ? -1 : areInIncreasingOrder(b, a) ? 1 : 0));
}

/** `[Double].sorted()`. */
export function sortedNumbers(xs: readonly number[]): number[] {
  return sortedBy(xs, (a, b) => a < b);
}

/** A Swift `Set<String>` rendered sorted. Measured: `["c10", "c2", "c1", "c0", "c11"].sorted()` → c0, c1, c10, c11,
 *  c2, the same order the default `Array.prototype.sort` gives. */
export function sortedStrings(xs: Iterable<string>): string[] {
  return [...xs].sort();
}

/** `reduce(0, +)`: left to right, starting from zero. */
export function sum(xs: readonly number[]): number {
  let s = 0;
  for (const x of xs) s = s + x;
  return s;
}

// ── Currency ────────────────────────────────────────────────────────────────────────────────────────────────────────

/** A non-negative magnitude to a whole number, exact ties to even. `v - floor(v)` is exact for every double. */
function roundHalfEven(v: number): number {
  const f = Math.floor(v);
  const d = v - f;
  if (d < 0.5) return f;
  if (d > 0.5) return f + 1;
  return f % 2 === 0 ? f : f + 1;
}

function grouped(whole: number): string {
  return BigInt(whole).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}

/** `Double.currencyString` (Models.swift:3150): NumberFormatter `.currency`, USD, `maximumFractionDigits = 0`. Measured in
 *  en_US: the default roundingMode is `.halfEven` (raw value 4) — 0.5 → "$0", 1.5 → "$2", 2.5 → "$2", 250.5 → "$250",
 *  251.5 → "$252", 1234.4999999999998 → "$1,234", 999999.5 → "$1,000,000"; thousands are grouped with commas;
 *  -1234.5 → "-$1,234"; -0.4 and -0.0 → "-$0". */
export function currencyString(x: number): string {
  if (!Number.isFinite(x)) throw new RangeError(`currencyString: ${x} is not finite`);
  const negative = x < 0 || Object.is(x, -0);
  return `${negative ? "-" : ""}$${grouped(roundHalfEven(Math.abs(x)))}`;
}

/** The second formatter in `CompAdjustment.rateString` (CompAdjustment.swift:348-353): `.currency`, USD, exactly two
 *  fraction digits, applied to `cents / 100` where `cents` is already whole. Measured: 156.24 → "$156.24", 0.5 →
 *  "$0.50", 1234.56 → "$1,234.56", 12345.07 → "$12,345.07", 100.1 → "$100.10". The digits are read off the whole
 *  `cents`, which is the decimal each of those doubles printed as. */
export function currencyStringOfCents(cents: number): string {
  if (!Number.isInteger(cents)) throw new RangeError(`currencyStringOfCents: ${cents} is not a whole number of cents`);
  const negative = cents < 0 || Object.is(cents, -0);
  const magnitude = Math.abs(cents);
  const dollars = Math.floor(magnitude / 100);
  const rem = magnitude - dollars * 100;
  return `${negative ? "-" : ""}$${grouped(dollars)}.${String(rem).padStart(2, "0")}`;
}

// ── printf ──────────────────────────────────────────────────────────────────────────────────────────────────────────

/** `String(format: "%.1f", x)`: the exact binary value, exact ties to even. Measured: 0.25 → "0.2", 0.75 → "0.8",
 *  1.25 → "1.2", 2.25 → "2.2", 5.25 → "5.2", 12.75 → "12.8"; off-tie values follow their binary expansion: 0.05 → "0.1",
 *  0.15 → "0.1", 0.35 → "0.3", 0.45 → "0.5", 2.35 → "2.4", 3.05 → "3.0", 0.95 → "0.9". `toFixed(1)` printed the same
 *  for every off-tie value and broke the ties upward (0.25 → "0.3", 1.25 → "1.3"). A double sits exactly halfway between
 *  two tenths only when it is an odd multiple of 1/4, so those alone are rounded here. */
export function formatOneDecimal(x: number): string {
  if (!Number.isFinite(x)) throw new RangeError(`formatOneDecimal: ${x} is not finite`);
  const quarters = x * 4;
  if (Number.isInteger(quarters) && Math.abs(quarters) % 2 === 1) {
    const lower = Math.floor(x * 10);
    const tenths = lower % 2 === 0 ? lower : lower + 1;
    const magnitude = Math.abs(tenths);
    return `${tenths < 0 ? "-" : ""}${Math.floor(magnitude / 10)}.${magnitude % 10}`;
  }
  return x.toFixed(1);
}

/** `String(format: "%.2f", x)` for the values `percentString` reaches: `x` is always `k / 100` for a whole `k`, because
 *  its caller built it as `(pct * 100).rounded() / 100`. The nearest two-decimal form of `k / 100` is `k / 100` itself,
 *  so printf and `toFixed(2)` agree by construction — there is no tie to break, which is the one thing they disagree
 *  about (`formatOneDecimal` above). Refuses anything else, rather than quietly standing in for a general `%.2f`. */
export function formatTwoDecimalsOfHundredths(x: number): string {
  if (!Number.isFinite(x)) throw new RangeError(`formatTwoDecimalsOfHundredths: ${x} is not finite`);
  if (!Number.isInteger(swiftRounded(x * 100)) || Math.abs(x * 100 - swiftRounded(x * 100)) > 1e-9) {
    throw new RangeError(`formatTwoDecimalsOfHundredths: ${x} is not a whole number of hundredths`);
  }
  return x.toFixed(2);
}

/** `SubjectProperty.percentString(_:)` (Models.swift:1177-1174) — a fraction as the app prints it: a whole percent when
 *  the value is whole (0.40 → "40%"), one decimal when one is enough (0.298 → "29.8%"), two when the value needs them
 *  (0.2976 → "29.76%").
 *
 *  THE ONE WRITER FOR EVERY RATIO PERCENTAGE THE APP SHOWS, and v2's second copy of it. It is here rather than in the
 *  ratio module because it is a Swift formatter, which is what this file is for; `functions/src/v2/resolve/ratio.ts`
 *  composes basis sentences with it, and the basis sentences are pinned character for character against Swift, so a
 *  disagreement of one digit fails there. */
export function percentString(ratio: number): string {
  if (!Number.isFinite(ratio)) throw new RangeError(`percentString: ${ratio} is not finite`);
  const pct = ratio * 100;
  const hundredths = swiftRounded(pct * 100) / 100;
  if (hundredths === swiftRounded(hundredths)) return `${swiftInt(hundredths)}%`;
  const tenths = swiftRounded(hundredths * 10) / 10;
  return tenths === hundredths ? `${formatOneDecimal(hundredths)}%` : `${formatTwoDecimalsOfHundredths(hundredths)}%`;
}

// ── Dates ───────────────────────────────────────────────────────────────────────────────────────────────────────────

/** The twelve month names `DateFormatter` writes in en_US, in order. Written out rather than read from `Intl`, because
 *  `Intl`'s month names are its own data and this file's job is to reproduce Foundation's. */
const MONTH_NAMES: readonly string[] = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

/** `DateFormatter` with `dateStyle = .long` and `timeStyle = .none`, in en_US: "June 30, 2026" (ProtestWindow.swift
 *  `longDate`). Takes the calendar day itself rather than an instant, because the day is what the sentence states and
 *  turning an instant into a day is the caller's decision — see `functions/src/v2/resolve/protestWindow.ts`, where which
 *  time zone that happens in is the whole question. */
export function longDateString(year: number, month: number, day: number): string {
  if (!Number.isInteger(month) || month < 1 || month > 12) throw new RangeError(`longDateString: month ${month}`);
  if (!Number.isInteger(day) || day < 1 || day > 31) throw new RangeError(`longDateString: day ${day}`);
  if (!Number.isInteger(year)) throw new RangeError(`longDateString: year ${year}`);
  return `${MONTH_NAMES[month - 1]} ${day}, ${year}`;
}

/** `YYYY-MM-DD`, the contract's `SourcedDate.date` shape (schema.ts ISO_DATE). */
export function isoDateString(year: number, month: number, day: number): string {
  if (!Number.isInteger(month) || month < 1 || month > 12) throw new RangeError(`isoDateString: month ${month}`);
  if (!Number.isInteger(day) || day < 1 || day > 31) throw new RangeError(`isoDateString: day ${day}`);
  if (!Number.isInteger(year) || year < 1000 || year > 9999) throw new RangeError(`isoDateString: year ${year}`);
  return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

// ── Strings ─────────────────────────────────────────────────────────────────────────────────────────────────────────

/** `CharacterSet.whitespacesAndNewlines`, which Apple documents as general category Z* plus U+0009–U+000D and U+0085.
 *  Measured: U+0085 is trimmed and U+FEFF is not — JS `trim()` does the opposite on both.
 *  MEASURED OVER EVERY UNICODE SCALAR (W2.4a1, 2026-09-15, same Swift and Foundation): `trimmingCharacters(in:
 *  .whitespacesAndNewlines)` removes exactly 26 scalars, each alone and from either end of a string, and they are the
 *  documented 25 plus U+200B ZERO WIDTH SPACE, which is Cf in current Unicode. W1's set had the 25; without U+200B a
 *  condominium unit typed as a lone U+200B read as given here and as empty in the app (functions/src/v2/resolve/). */
const WHITESPACE_AND_NEWLINES: ReadonlySet<number> = new Set<number>([
  0x09, 0x0a, 0x0b, 0x0c, 0x0d, 0x20, 0x85, 0xa0, 0x1680,
  0x2000, 0x2001, 0x2002, 0x2003, 0x2004, 0x2005, 0x2006, 0x2007, 0x2008, 0x2009, 0x200a, 0x200b,
  0x2028, 0x2029, 0x202f, 0x205f, 0x3000,
]);

const isSwiftWhitespace = (ch: string): boolean => WHITESPACE_AND_NEWLINES.has(ch.codePointAt(0) ?? -1);

/** `String.trimmed` (Models.swift:3145): `trimmingCharacters(in: .whitespacesAndNewlines)`, by Unicode scalar. */
export function trimmed(s: string): string {
  const scalars = Array.from(s);
  let start = 0;
  let end = scalars.length;
  while (start < end && isSwiftWhitespace(scalars[start])) start++;
  while (end > start && isSwiftWhitespace(scalars[end - 1])) end--;
  return scalars.slice(start, end).join("");
}

/** `String.uppercased()`. Measured: "ß" → "SS", as `toUpperCase` also gives. */
export function uppercased(s: string): string {
  return s.toUpperCase();
}

type GraphemeSegmenter = new (locale: string, options: { granularity: "grapheme" }) => {
  segment(input: string): Iterable<{ readonly segment: string }>;
};

/** `String.count`: Characters, not UTF-16 units. Measured: composed "É" and decomposed "E\u{301}" both count 1. */
export function characterCount(s: string): number {
  const Segmenter = (Intl as unknown as { Segmenter?: GraphemeSegmenter }).Segmenter;
  if (Segmenter === undefined) throw new Error("characterCount: Intl.Segmenter is unavailable in this runtime");
  let n = 0;
  for (const segment of new Segmenter("en", { granularity: "grapheme" }).segment(s)) {
    if (segment !== undefined) n++;
  }
  return n;
}

// ── The strings PropertyTypeSupport's matchers read (W2.4a1) ────────────────────────────────────────────────────────
// Each claim below was measured by Swift probes on this machine (swift 6.3.3, macOS 26 Foundation, 2026-09-15) running the
// app's own expressions, and, where it says so, over every Unicode scalar. The simulator the Swift recorder (W2.4c) runs
// on is the truth where the two differ.

/** `String.lowercased()`, one Unicode scalar at a time. Measured: "ΣΣ" → "σσ" (Swift applies no final-sigma rule, where
 *  `toLowerCase` gives "σς"), "İ" (U+0130) → "i" + U+0307, "K" (U+212A) → "k". Over every scalar, each lowercases here as
 *  in Swift except 28 that this runtime lowercases and Swift leaves unchanged: U+A7CE, U+A7D2, U+A7D4 and U+16EA0–U+16EB8,
 *  none of them ASCII and none lowercasing to ASCII. */
export function lowercased(s: string): string {
  let out = "";
  for (const scalar of s) out += scalar.toLowerCase();
  return out;
}

/** A string's Characters (extended grapheme clusters), in order. */
export function characters(s: string): string[] {
  const Segmenter = (Intl as unknown as { Segmenter?: GraphemeSegmenter }).Segmenter;
  if (Segmenter === undefined) throw new Error("characters: Intl.Segmenter is unavailable in this runtime");
  return Array.from(new Segmenter("en", { granularity: "grapheme" }).segment(s), (part) => part.segment);
}

/** `==` on two Strings or two Characters, which Swift documents as canonical equivalence; compared here by canonical
 *  decomposition. */
export function canonicallyEqual(a: string, b: string): boolean {
  return a.normalize("NFD") === b.normalize("NFD");
}

/** `Character.isLetter`, which Swift defines on the Character's FIRST Unicode scalar: true when that scalar's general
 *  category is one of the five Letter categories (Lu, Ll, Lt, Lm, Lo), which is `\p{L}`. Taking the first scalar rather
 *  than the whole cluster is what makes "e" + U+0301 a letter — the combining mark is not one on its own. */
export function isSwiftLetter(character: string): boolean {
  const first = Array.from(character)[0];
  return first !== undefined && /\p{L}/u.test(first);
}

/** `Character.isNumber`, the same way: the first scalar's general category is Nd, Nl or No, which is `\p{N}`. Swift's
 *  own documentation separates this from `isWholeNumber` and from `isASCII`; a Roman numeral and a superscript two are
 *  numbers here, as they are there. */
export function isSwiftNumber(character: string): boolean {
  const first = Array.from(character)[0];
  return first !== undefined && /\p{N}/u.test(first);
}

/** `StringProtocol.contains(_:)` with a String argument, as PropertyTypeSupport's matchers call it: `needle`'s Characters
 *  occur in `haystack` as a run of whole Characters, each canonically equal. Measured: "condo" + U+0301 does not contain
 *  "condo", which `includes` says it does, because U+0301 joins the "o" into one Character. Over every scalar appended to,
 *  prepended to and inserted into "condo", "manufact" and "mfg", through the app's matchers, this agrees with Swift except
 *  on 43 scalars, none ASCII, that join a neighbouring Character in one runtime's Unicode data and not the other's:
 *  U+1ACF–U+1ADD, U+1AE0–U+1AEB, U+10EFA–U+10EFB, U+11A3A, U+11B60–U+11B67, U+1E6E3, U+1E6E6, U+1E6EE–U+1E6EF, U+1E6F5. */
export function containsCharacters(haystack: string, needle: string): boolean {
  const h = characters(haystack).map((c) => c.normalize("NFD"));
  const n = characters(needle).map((c) => c.normalize("NFD"));
  outer: for (let i = 0; i + n.length <= h.length; i++) {
    for (let j = 0; j < n.length; j++) if (h[i + j] !== n[j]) continue outer;
    return true;
  }
  return false;
}
